import { useEffect, useState } from 'react';
import { officialRequestsAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
    ChevronLeft, Paperclip, Download, Loader2, Mail, MailCheck, AlertCircle,
    Clock, CheckCircle2, Send, User, Users, Calendar,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_META = {
    nouvelle: { label: 'Nouvelle', className: 'bg-amber-100 text-amber-800 border-amber-200', icon: AlertCircle },
    en_cours: { label: 'En cours', className: 'bg-blue-100 text-blue-800 border-blue-200', icon: Clock },
    traitee: { label: 'Traitée', className: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: CheckCircle2 },
};

const humanSize = (b) => {
    if (b < 1024) return `${b} o`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} Ko`;
    return `${(b / (1024 * 1024)).toFixed(1)} Mo`;
};

export default function OfficialRequestDetail({ item, currentUser, isDirigeantView, onBack, onUpdated }) {
    const [reply, setReply] = useState(item?.reply || '');
    const [status, setStatus] = useState(item?.status || 'nouvelle');
    const [saving, setSaving] = useState(false);
    const [downloadingId, setDownloadingId] = useState(null);

    useEffect(() => {
        setReply(item?.reply || '');
        setStatus(item?.status || 'nouvelle');
    }, [item?.id, item?.reply, item?.status]);

    // Marquer comme lu (VP côté réponse) au premier affichage si applicable
    useEffect(() => {
        if (!item) return;
        if (!isDirigeantView && item.status === 'traitee' && !item.read_by_sender_at) {
            officialRequestsAPI.markRead(item.id).then(() => onUpdated?.()).catch(() => {});
        }
    }, [item?.id, item?.status, item?.read_by_sender_at, isDirigeantView, onUpdated, item]);

    if (!item) return null;
    const meta = STATUS_META[status] || STATUS_META.nouvelle;
    const StatusIcon = meta.icon;

    const downloadAttachment = async (att) => {
        setDownloadingId(att.id);
        try {
            const res = await officialRequestsAPI.downloadAttachment(item.id, att.id);
            const blob = new Blob([res.data], { type: att.mime || 'application/octet-stream' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = att.filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error("Erreur lors du téléchargement");
        } finally {
            setDownloadingId(null);
        }
    };

    const saveChanges = async (nextStatus, includeReply) => {
        setSaving(true);
        try {
            const payload = {};
            if (nextStatus !== undefined) payload.status = nextStatus;
            if (includeReply) payload.reply = reply.trim();
            await officialRequestsAPI.update(item.id, payload);
            toast.success("Demande mise à jour");
            onUpdated?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur lors de la mise à jour");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-4">
            <Button
                variant="ghost"
                size="sm"
                onClick={onBack}
                data-testid="or-detail-back"
                className="text-slate-600"
            >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Retour à la liste
            </Button>

            {/* Header demande */}
            <Card className="border-slate-200">
                <CardContent className="p-5 space-y-3">
                    <div className="flex items-start gap-3 flex-wrap">
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-2">
                                <Badge variant="outline" className={meta.className}>
                                    <StatusIcon className="w-3 h-3 mr-1" />
                                    {meta.label}
                                </Badge>
                                <Badge variant="outline" className="bg-slate-50">{item.subject_label}</Badge>
                            </div>
                            <h2 className="text-xl font-bold text-slate-900" data-testid="or-detail-title">{item.title}</h2>
                            <div className="mt-2 flex items-center gap-4 text-xs text-slate-500 flex-wrap">
                                <span className="flex items-center gap-1"><User className="w-3 h-3" /> De <strong className="text-slate-700">{item.sender_name}</strong></span>
                                <span className="flex items-center gap-1"><Users className="w-3 h-3" /> À <strong className="text-slate-700">{(item.recipient_names || []).join(', ')}</strong></span>
                                <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(item.created_at).toLocaleString('fr-FR')}</span>
                            </div>
                        </div>
                    </div>

                    <div className="pt-2 border-t border-slate-100">
                        <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed" data-testid="or-detail-message">
                            {item.message}
                        </p>
                    </div>

                    {(item.attachments || []).length > 0 && (
                        <div className="pt-3 border-t border-slate-100">
                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                                Pièces jointes ({item.attachments.length})
                            </p>
                            <ul className="space-y-1.5">
                                {item.attachments.map((att) => (
                                    <li key={att.id} className="flex items-center gap-2 text-sm bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
                                        <Paperclip className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                                        <span className="flex-1 truncate">{att.filename}</span>
                                        <span className="text-xs text-slate-500">{humanSize(att.size || 0)}</span>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => downloadAttachment(att)}
                                            disabled={downloadingId === att.id}
                                            data-testid={`or-att-download-${att.id}`}
                                        >
                                            {downloadingId === att.id
                                                ? <Loader2 className="w-3 h-3 animate-spin" />
                                                : <Download className="w-3 h-3" />}
                                        </Button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Zone traitement/réponse — DIRIGEANT */}
            {isDirigeantView && (
                <Card className="border-indigo-200 bg-indigo-50/30">
                    <CardContent className="p-5 space-y-3">
                        <div className="flex items-center gap-2">
                            <MailCheck className="w-5 h-5 text-indigo-700" />
                            <h3 className="font-semibold text-slate-900">Traiter la demande</h3>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {['nouvelle', 'en_cours', 'traitee'].map((s) => (
                                <button
                                    key={s}
                                    onClick={() => { setStatus(s); saveChanges(s, false); }}
                                    disabled={saving}
                                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${status === s ? STATUS_META[s].className + ' ring-2 ring-offset-1 ring-indigo-300' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                                    data-testid={`or-set-status-${s}`}
                                >
                                    {STATUS_META[s].label}
                                </button>
                            ))}
                        </div>
                        <div>
                            <Label htmlFor="or-reply">Réponse écrite (transmise au VP)</Label>
                            <Textarea
                                id="or-reply"
                                data-testid="or-reply-input"
                                rows={5}
                                value={reply}
                                onChange={(e) => setReply(e.target.value)}
                                placeholder="Rédigez votre réponse ici. Elle sera visible par le VP dans son onglet Demandes officielles et envoyée par email."
                                className="mt-1"
                            />
                            <div className="mt-2 flex justify-end">
                                <Button
                                    onClick={() => saveChanges(reply.trim() ? 'traitee' : status, true)}
                                    disabled={saving || !reply.trim()}
                                    className="bg-indigo-600 hover:bg-indigo-700 text-white"
                                    data-testid="or-send-reply-btn"
                                >
                                    {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
                                    Envoyer la réponse
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Zone lecture réponse — VP */}
            {!isDirigeantView && item.reply && (
                <Card className="border-emerald-200 bg-emerald-50/30" data-testid="or-detail-reply-card">
                    <CardContent className="p-5">
                        <div className="flex items-center gap-2 mb-2">
                            <MailCheck className="w-5 h-5 text-emerald-700" />
                            <h3 className="font-semibold text-slate-900">Réponse — {item.replied_by_name}</h3>
                            {item.replied_at && (
                                <span className="text-xs text-slate-500">
                                    · {new Date(item.replied_at).toLocaleString('fr-FR')}
                                </span>
                            )}
                        </div>
                        <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">
                            {item.reply}
                        </p>
                    </CardContent>
                </Card>
            )}
            {!isDirigeantView && !item.reply && item.status !== 'traitee' && (
                <div className="text-center text-sm text-slate-500 py-4 flex items-center justify-center gap-2">
                    <Mail className="w-4 h-4" />
                    En attente de traitement par le dirigeant.
                </div>
            )}
        </div>
    );
}
